import React from 'react';
import { Badge, ClassNameMap, IconButton } from '@mui/material';
import { Search,
  ContactSupportOutlined,
  Contacts, ContactsOutlined,
  DateRange, DateRangeOutlined,
  MonetizationOn, MonetizationOnOutlined, SettingsOutlined, AccountBoxOutlined, Settings, AccountBox,
  MailOutline,
  LocationCity,
  LocationCityOutlined,
  People, PeopleOutlined,
  PersonAddOutlined,
  DescriptionOutlined,
  Description,
} from '@mui/icons-material';
import { ROUTING_CONFIG } from '@uplift/constants';

import {
  ContactSupportIcon,
  HomeIcon, HomeOutlinedIcon, LogOutIcon,
} from '@uplift/icons';
import { useFeatureFlag, useL10n, useTabsSyncedCallbackTrigger } from '@uplift/hooks';

import { AutoLogoutTrigger } from '../../../../molecules/Modals/auto-logout-notification';

import { ProviderMobileNavigationMenu } from './ProviderMobileNavigationMenu.component';
import { MobileNavigationLink } from './MobileNavigationLink.component';
import { StickyStackItem } from '../../../StickyStack';
import { MobilePatientsSearchDialog } from './MobilePatientsSearchDialog.component';
import { Navigation, NavigationLogo, NavigationProps, NavigationSection } from '../../../components';
import { useMessagingContext } from '../../../../Messaging';
import { useCanAccessPracticeStates } from '../../../../PracticeStates/hooks';
import { ReferProviderModal } from '../ProviderDesktopNavigation';
import { SignOnBonusReferralSource } from '@uplift/api';


export type ProviderMobileNavigationProps = {
  logoLink?: string;
  avatarSrc?: string;
  providerId?: string;
  classes?: ClassNameMap<'root'>;
  variant?: NavigationProps['variant'];
  showClientsSearch?: boolean;
  showLinks?: boolean;
  showFinanceLink?: boolean;
  showSuperviseesLink?: boolean;
  showProfile?: boolean;
  showProfileLinks?: boolean;
  showMessages?: boolean;
  overrideStart?: React.ReactNode;
};


export const ProviderMobileNavigation: React.FC<ProviderMobileNavigationProps> = ({
  providerId,
  logoLink = ROUTING_CONFIG.main,
  showClientsSearch = true,
  showLinks = true,
  showFinanceLink = true,
  showSuperviseesLink = false,
  showProfileLinks = true,
  overrideStart = null,
  showMessages = true,
  ...props
}) => {
  const l10n = useL10n();
  const messaging = useMessagingContext();
  const [ isSearchOpened, setIsSearchOpened ] = React.useState(false);
  const logoutTrigger = useTabsSyncedCallbackTrigger(AutoLogoutTrigger.logoutManual);
  const { canAccess: canAccessPracticeStates } = useCanAccessPracticeStates();
  const PROVIDER_DOCUMENT_CENTER = useFeatureFlag('PROVIDER_DOCUMENT_CENTER');
  const [ referModalOpen, setReferModalOpen ] = React.useState(false);

  return (
    <StickyStackItem order={0} placement="top">
      <Navigation position="sticky" variant={props.variant}>
        <NavigationSection alignment="start">
          {overrideStart ?? <NavigationLogo href={logoLink} />}
        </NavigationSection>

        <NavigationSection alignment="end" vertical="center">
          {showMessages && (
            <IconButton onClick={() => messaging.openModal()}>
              <Badge badgeContent={messaging.unreadMessagesCount} color="error">
                <MailOutline />
              </Badge>
            </IconButton>
          )}
          {showClientsSearch && (
            <IconButton onClick={() => setIsSearchOpened(true)}>
              <Search />
            </IconButton>
          )}
          <ProviderMobileNavigationMenu>
            {showLinks && (
              <>
                <MobileNavigationLink
                  title={l10n.pages.main}
                  icon={HomeOutlinedIcon}
                  iconActive={HomeIcon}
                  path={ROUTING_CONFIG.main}
                />
                <MobileNavigationLink
                  title={l10n.schedule.createAppointment.schedule}
                  icon={DateRangeOutlined}
                  iconActive={DateRange}
                  path={ROUTING_CONFIG.schedule}
                />
                {showFinanceLink && (
                  <MobileNavigationLink
                    title={l10n.finance.title}
                    icon={MonetizationOnOutlined}
                    iconActive={MonetizationOn}
                    path={ROUTING_CONFIG.finance}
                  />
                )}
                <MobileNavigationLink
                  title={l10n.pages.clients}
                  icon={ContactsOutlined}
                  iconActive={Contacts}
                  path={ROUTING_CONFIG.patients}
                  exact
                />
                {showSuperviseesLink && (
                  <MobileNavigationLink
                    title="Supervisees"
                    icon={PeopleOutlined}
                    iconActive={People}
                    path={ROUTING_CONFIG.supervisees}
                    exact
                  />
                )}
                <MobileNavigationLink
                  title={l10n.pages.contactSupport}
                  icon={ContactSupportOutlined}
                  iconActive={ContactSupportIcon}
                  path={ROUTING_CONFIG.support}
                />
              </>
            )}
            {showProfileLinks && (
              <>
                <MobileNavigationLink
                  title={l10n.providerSettings.onboarding.matchingProfile}
                  icon={AccountBoxOutlined}
                  iconActive={AccountBox}
                  path={ROUTING_CONFIG.profile}
                />
                <MobileNavigationLink
                  title={l10n.general.account}
                  icon={SettingsOutlined}
                  iconActive={Settings}
                  path={ROUTING_CONFIG.settings}
                />
                {canAccessPracticeStates && (
                  <MobileNavigationLink
                    title={l10n.providerSettings.onboarding.practiceStates}
                    icon={LocationCityOutlined}
                    iconActive={LocationCity}
                    path={ROUTING_CONFIG.practiceStates}
                  />
                )}

                {PROVIDER_DOCUMENT_CENTER && (
                  <MobileNavigationLink
                    title="Documents Center"
                    icon={DescriptionOutlined}
                    iconActive={Description}
                    path={ROUTING_CONFIG.documentsCenter}
                  />
                )}

                <MobileNavigationLink
                  title="Refer a Provider"
                  icon={PersonAddOutlined}
                  onClick={() => setReferModalOpen(true)}
                />
              </>
            )}
            <MobileNavigationLink title="Log Out" icon={LogOutIcon} onClick={() => logoutTrigger()} />
          </ProviderMobileNavigationMenu>
        </NavigationSection>
        <MobilePatientsSearchDialog
          open={isSearchOpened}
          onClose={() => setIsSearchOpened(false)}
        />
        {providerId && <ReferProviderModal source={SignOnBonusReferralSource.PROVIDER} providerId={providerId} open={referModalOpen} onClose={() => setReferModalOpen(false)} />}
      </Navigation>
    </StickyStackItem>
  );
};
